import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import ConveniosService from "../../services/ConveniosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox, mdiFileExcel } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  OutlinedInput,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const PacientesEmpresas = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = usuarioString ? JSON.parse(usuarioString) : {};

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros principales
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState("todas");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Pacientes de Empresas";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resConv] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        ConveniosService.getConvenios().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaConv = Array.isArray(resConv?.data?.convenios)
        ? resConv.data.convenios
        : Array.isArray(resConv?.data)
        ? resConv.data
        : [];

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setConvenios(listaConv);

      // Sucursal por defecto: usuario actual o todas
      const uSucId = currentU.idSucursal || currentU.id_sucursal || currentU.sucursalId;
      const sucValida = listaSuc.find((s) => String(s.idSucursal || s.id) === String(uSucId));
      if (sucValida) {
        setSucursalSeleccionada(String(sucValida.idSucursal || sucValida.id));
      } else {
        setSucursalSeleccionada("todas");
      }
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte de pacientes de empresas.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }
    return null;
  };

  const isCancelada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "C" || est === "CANCELADA" || Boolean(sol.motivoCancelacion && String(sol.motivoCancelacion).trim() !== "");
  };

  // --- FILTRADO Y CONTEO POR EMPRESA ---
  const datosEmpresas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);

    if (!start || !end) return [];

    // 1. Filtrar solicitudes activas en el rango y sucursal seleccionada
    const solicitudesFiltradas = solicitudes.filter((sol) => {
      // Debe tener empresa/convenio obligatoriamente
      const tieneEmpresa = Boolean(sol.idConvenio || (sol.nombreConvenio && sol.nombreConvenio.trim() !== ""));
      if (!tieneEmpresa) return false;

      // Excluir canceladas
      if (isCancelada(sol)) return false;

      // Filtro de sucursal
      if (sucursalSeleccionada !== "todas") {
        const sId = sol.idSucursal || sol.id_sucursal;
        if (String(sId) !== String(sucursalSeleccionada)) return false;
      }

      // Filtro de fecha
      const dtSol = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
      if (!dtSol) return false;
      if (dtSol.getTime() < start.getTime() || dtSol.getTime() > end.getTime()) return false;

      return true;
    });

    // 2. Mapear empresas/convenios a mostrar
    let empresasAProcesar = convenios;
    if (empresaSeleccionada !== "todas") {
      empresasAProcesar = convenios.filter((c) => String(c.idConvenio || c.id) === String(empresaSeleccionada));
    }

    // Mapa para acumular solicitudes por ID o Nombre de Convenio
    const mapaConteo = {};
    empresasAProcesar.forEach((conv) => {
      const cId = String(conv.idConvenio || conv.id);
      const cNom = conv.nombre || conv.nombreConvenio || conv.empresa || "Empresa Sin Nombre";
      mapaConteo[cId] = {
        id: cId,
        nombre: cNom,
        pacientes: 0,
      };
    });

    // Contar solicitudes pertenecientes a cada empresa
    solicitudesFiltradas.forEach((sol) => {
      const cIdSol = sol.idConvenio || sol.id_convenio;
      if (cIdSol && mapaConteo[String(cIdSol)]) {
        mapaConteo[String(cIdSol)].pacientes += 1;
      } else if (sol.nombreConvenio) {
        const nomNormSol = (sol.nombreConvenio || "").toLowerCase().trim();
        const foundId = Object.keys(mapaConteo).find(
          (k) => mapaConteo[k].nombre.toLowerCase().trim() === nomNormSol
        );
        if (foundId) {
          mapaConteo[foundId].pacientes += 1;
        } else if (empresaSeleccionada === "todas") {
          // Si es una empresa que no estaba en el catálogo, agregarla dinámicamente
          mapaConteo[sol.nombreConvenio] = {
            id: sol.nombreConvenio,
            nombre: sol.nombreConvenio,
            pacientes: 1,
          };
        }
      }
    });

    return Object.values(mapaConteo);
  }, [solicitudes, sucursalSeleccionada, empresaSeleccionada, fechaDe, fechaA, convenios]);

  // Total General acumulado de pacientes/solicitudes
  const totalGeneralPacientes = useMemo(() => {
    return datosEmpresas.reduce((acc, emp) => acc + emp.pacientes, 0);
  }, [datosEmpresas]);

  // --- EXPORTAR A PDF ---
  const handleExportarPDF = async () => {
    if (datosEmpresas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen datos de empresas para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("landscape", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales.find((s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada)) || sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    doc.setFillColor(24, 35, 79);
    doc.rect(202, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 242.5, startY + 5, { align: "center" });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 283, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 283, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 283, currY, { align: "right" });

    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("PACIENTES DE EMPRESAS", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Reporte generado del ${fechaDe} al ${fechaA}`, 14, currY + 5);

    let nextY = currY + 12;

    const bodyPDF = datosEmpresas.map((emp) => [emp.nombre, emp.pacientes]);
    bodyPDF.push([
      { content: "TOTAL GENERAL:", styles: { fontStyle: "bold", halign: "left" } },
      { content: totalGeneralPacientes, styles: { fontStyle: "bold", halign: "center" } },
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          { content: "Empresa", styles: { halign: "left" } },
          { content: "Pacientes", styles: { halign: "center" } },
        ],
      ],
      body: bodyPDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 2.5, halign: "left", valign: "middle" },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "center" },
      },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 200);
      doc.text(`Página ${i} de ${pageCount}`, 283, 200, { align: "right" });
    }

        doc.save(`Reporte_Pacientes_Empresas_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Pacientes por Empresa en PDF...",
      "Procesando documento"
    );
  };

  // --- EXPORTAR A EXCEL ---
  const handleExportarExcel = () => {
    if (datosEmpresas.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen datos de empresas para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    const rowsExcel = [["Empresa", "Pacientes"]];
    datosEmpresas.forEach((emp) => {
      rowsExcel.push([emp.nombre, emp.pacientes]);
    });
    rowsExcel.push(["TOTAL GENERAL", totalGeneralPacientes]);

    const worksheet = XLSX.utils.aoa_to_sheet(rowsExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pacientes de Empresas");

    XLSX.writeFile(workbook, `Reporte_Pacientes_Empresas_${fechaDe}_al_${fechaA}.xlsx`);
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE PACIENTES DE EMPRESAS
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "100%", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal y Empresa */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Empresa:
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={empresaSeleccionada}
                          onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                        >
                          <MenuItem value="todas">Todas las empresas</MenuItem>
                          {convenios.map((conv) => (
                            <MenuItem key={conv.idConvenio || conv.id} value={String(conv.idConvenio || conv.id)}>
                              {conv.nombre || conv.nombreConvenio || conv.empresa}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="De:"
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label="A:"
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <IconButton
                            onClick={fetchDatosIniciales}
                            sx={{ backgroundColor: "#006787", color: "white", "&:hover": { backgroundColor: "#014e65" } }}
                            title="Actualizar Datos"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Título informativo antes de la tabla */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#18234f" }}>
                  Reporte generado del {fechaDe} al {fechaA}
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={3} sx={{ width: "100%" }}>
                  {/* TABLA PRINCIPAL DE EMPRESAS Y PACIENTES */}
                  <Paper elevation={2} sx={{ p: 0, borderRadius: 3, overflow: "hidden", backgroundColor: "#ffffff", width: "100%" }}>
                    <Table size="small" sx={{ width: "100%", border: "1px solid #e2e8f0" }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>Empresa</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem", width: "200px" }}>Pacientes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {datosEmpresas.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 4, color: "#64748b" }}>
                              No se encontraron registros de pacientes de empresas en las fechas seleccionadas ({fechaDe} al {fechaA}).
                            </TableCell>
                          </TableRow>
                        ) : (
                          datosEmpresas.map((emp) => (
                            <TableRow key={emp.id} sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                              <TableCell sx={{ fontWeight: "bold", color: "#1e293b", fontSize: "0.9rem" }}>{emp.nombre}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.95rem" }}>
                                {emp.pacientes}
                              </TableCell>
                            </TableRow>
                          ))
                        )}

                        {/* Fila Total General */}
                        {datosEmpresas.length > 0 && (
                          <TableRow sx={{ backgroundColor: "#f0f4f8", borderTop: "2px solid #006787" }}>
                            <TableCell sx={{ fontWeight: "bold", color: "#18234f", fontSize: "1rem" }}>
                              TOTAL GENERAL:
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "1.1rem" }}>
                              {totalGeneralPacientes}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botones flotantes laterales derechos para exportar PDF y Excel */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          {/* Botón PDF */}
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>

          {/* Botón Excel */}
          <Tooltip
            title="Exportar Reporte a Excel"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#2e7d32",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.5} color="#2e7d32" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default PacientesEmpresas;
